import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { apiGet, apiPost, apiPut, setApiToken } from "../lib/api";
import i18n from "../lib/i18n";
import { normalizeLocale, type SupportedLocale } from "../lib/locales";

export type TenantBootstrap = {
  user: { user_id: string; role: string; tenant_id: string };
  permissions: string[];
  tenant: {
    id: string;
    name: string;
    slug: string;
    default_locale: string;
    status: "active" | "suspended" | "onboarding";
    market_profile?: "default" | "telegram_first" | "whatsapp_first";
    primary_messenger?: "telegram" | "whatsapp" | "sms" | "email" | null;
    messenger_first_mode?: boolean;
    plan_code?: string | null;
    notes?: string | null;
  };
  branches: Array<{ id: string; name: string; slug: string }>;
  feature_flags: Record<string, boolean>;
  channel_capabilities?: Record<
    string,
    {
      label: string;
      supports_manager_embedded_app: boolean;
      supports_client_embedded_app: boolean;
      supports_freeform_inbound: boolean;
      supports_freeform_outbound: boolean;
      supports_interactive_buttons: boolean;
      template_based_outbound_only: boolean;
      supports_deep_links: boolean;
      supports_identity_linking: boolean;
      rollout_mode: string;
    }
  >;
  locale: SupportedLocale;
};

type TenantAuthContextValue = {
  token: string;
  bootstrap: TenantBootstrap | null;
  isLoading: boolean;
  login: (tenantId: string, email: string, password: string) => Promise<void>;
  loginWithToken: (accessToken: string) => void;
  updateTenantLocale: (locale: SupportedLocale) => Promise<void>;
  updateTenantMessagingStrategy: (payload: {
    market_profile?: "default" | "telegram_first" | "whatsapp_first";
    primary_messenger?: "telegram" | "whatsapp" | "sms" | "email" | null;
    messenger_first_mode?: boolean;
    apply_profile_defaults?: boolean;
  }) => Promise<void>;
  logout: () => void;
  can: (permission: string) => boolean;
};

const TenantAuthContext = createContext<TenantAuthContextValue | undefined>(undefined);
const TOKEN_KEY = "v3_tenant_token";

export function TenantAuthProvider({ children }: { children: React.ReactNode }) {
  const [token, setToken] = useState<string>(localStorage.getItem(TOKEN_KEY) ?? "");
  const [bootstrap, setBootstrap] = useState<TenantBootstrap | null>(null);
  const [isLoading, setLoading] = useState<boolean>(() => !!localStorage.getItem(TOKEN_KEY));

  useEffect(() => {
    setApiToken(token);
    if (!token) {
      setBootstrap(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    apiGet<TenantBootstrap>("/v3/bootstrap")
      .then((data) => setBootstrap(data))
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        setBootstrap(null);
      })
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    const locale = bootstrap?.tenant?.default_locale;
    if (!locale) return;
    i18n.changeLanguage(normalizeLocale(locale));
  }, [bootstrap?.tenant?.default_locale]);

  const value = useMemo<TenantAuthContextValue>(
    () => ({
      token,
      bootstrap,
      isLoading,
      async login(tenantId: string, email: string, password: string) {
        const data = await apiPost<{ access_token: string }>("/v3/auth/login", {
          tenant_id: tenantId,
          email,
          password
        });
        localStorage.setItem(TOKEN_KEY, data.access_token);
        setToken(data.access_token);
      },
      loginWithToken(accessToken: string) {
        localStorage.setItem(TOKEN_KEY, accessToken);
        setToken(accessToken);
      },
      async updateTenantLocale(locale: SupportedLocale) {
        const data = await apiPut<TenantBootstrap>("/v3/bootstrap/locale", { locale });
        setBootstrap(data);
      },
      async updateTenantMessagingStrategy(payload) {
        const data = await apiPut<TenantBootstrap>("/v3/bootstrap/channel-strategy", payload);
        setBootstrap(data);
      },
      logout() {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        setBootstrap(null);
      },
      can(permission: string) {
        return !!bootstrap?.permissions?.includes(permission);
      }
    }),
    [token, bootstrap, isLoading]
  );

  return <TenantAuthContext.Provider value={value}>{children}</TenantAuthContext.Provider>;
}

export function useTenantAuth() {
  const ctx = useContext(TenantAuthContext);
  if (!ctx) {
    throw new Error("Tenant auth context missing");
  }
  return ctx;
}

// Backward-compatible aliases for existing components.
export const AuthProvider = TenantAuthProvider;
export const useAuth = useTenantAuth;
